<template>
  <div class="vacancy-section-basic">
    <div class="vacancy-section-basic__section" data-edit-group="1">
      <TranslateFiled
        ref="input-name"
        :title="$tNewVacancy2('name')"
        :value="inputs['name'].value"
        :error="inputs['name'].error"
        :placeholder="$tNewVacancy2('placeholder_name')"
        :use-editor-headings="useEditorsHeadings"
        :use-translate-buttons="useFieldsTranslations"
        :original-locale="originalLocale"
        @input="changeAction('name', 'setInputValue', ['name', $event])"
        @blur="saveAction('name', ['name', $event])"
        class="vacancy-section-basic__translate-field"
      />
    </div>
    <div class="vacancy-section-basic__section edit-group" data-edit-group="1">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('profession') }}
        </h4>
      </div>
      <SCategory ref="input-profession"
                 :value="inputs['profession'].value"
                 :error="inputs['profession'].error"
                 :placeholder="$tNewVacancy2('placeholder_profession')"
                 class="mb-5"
                 @input="onProfessionInput"/>
      <TranslateFiled
        ref="input-custom_category"
        v-if="showCustomProfession"
        :label="$tNewVacancy2('Specify your profession')"
        :value="inputs['custom_category'].value"
        :error="inputs['custom_category'].error"
        :use-editor-headings="useEditorsHeadings"
        :use-translate-buttons="useFieldsTranslations"
        :original-locale="originalLocale"
        class="mb-1"
        @input="changeAction('custom_category', 'setInputValue', ['custom_category', $event])"
        @blur="saveAction('custom_category', ['custom_category', $event])"
      />

      <div ref="input-categories" class="edit-group__sub-content mt-4">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('categories') }}
          </span>
        </div>
        <div class="edit-group__card">
          <ButtonCheckList v-if="categories && categories.length"
                           :value="inputs['categories'].value"
                           :error="inputs['categories'].error"
                           :required="inputs['categories'].required"
                           :options="categories"
                           type="checkbox"
                           compareType="id"
                           text-key="title"
                           @input="changeAction('categories', 'setInputValue', ['categories', $event])"
                           class="edit-group__row _inline-grid _blue"/>
          <SDirection
            :label="$tNewVacancy2('add other categories')"
            searchable
            :optionsFilter="categoriesSelectFilter"
            @input="onAddCategory"
            :placeholder="$tNewVacancy2('placeholder_other_category')"
            class="vacancy-section-basic__category-select  _white  _shadow">
            <template v-slot:option="{option, getOptionLabel}">
              {{ getOptionLabel(option) }}
              <BaseSvg name="ico-plus"/>
            </template>
          </SDirection>
        </div>

        <BaseInputError :value="inputs['categories'].error" class="mt-1" />
      </div>
    </div>
    <div class="vacancy-section-basic__section edit-group" data-edit-group="1">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('Number of vacancies') }}
        </h4>
      </div>
      <Amount ref="input-quantity"
              :value="inputs['quantity'].value"
              :error="inputs['quantity'].error"
              :required="inputs['quantity'].required"
              :min="minQuantity"
              @input="changeAction('quantity', 'setInputValue', ['quantity', $event])"
      />
    </div>
    <div class="vacancy-section-basic__section _salary edit-group" data-edit-group="2">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('wage') }}
        </h4>
      </div>
      <div class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('salary_type') }}
          </span>
        </div>
        <div class="edit-group__card _small">
          <ButtonCheckList ref="input-salary_type"
                           :value="inputs['salary_type'].value"
                           :error="inputs['salary_type'].error"
                           :required="inputs['salary_type'].required"
                           :options="salaryTypes"
                           type="radio"
                           compareType="id"
                           text-key="name"
                           @input="changeAction('salary_type', 'setInputValue', ['salary_type', $event])"
                           class="edit-group__row _blue _three-columns"/>
        </div>
      </div>
      <div class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('Currency') }}
          </span>
        </div>
        <div class="edit-group__card _small">
          <ButtonCheckList ref="input-currency"
                           :value="inputs['currency'].value"
                           :error="inputs['currency'].error"
                           :required="inputs['currency'].required"
                           :options="currencies"
                           type="radio"
                           compareType="id"
                           text-key="currency"
                           @input="changeAction('currency', 'setInputValue', ['currency', $event])"
                           class="edit-group__row _blue _three-columns"/>
        </div>
      </div>
      <div class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('salary') }}
          </span>
          <SwitchCheckbox
            v-model="isExactSalary"
            :label="$tNewVacancy2('Specify exact salary')"
            @input="onSalaryExactVariantChange"
            class="_blue"
          />
        </div>
        <BaseRadioList
          ref="input-salary_brutto"
          :value="inputs['salary_brutto'].value"
          text-key="label"
          :options="salaryNetGrossOptions"
          class="mt-4 mb-3"
          :is-checked="(value, option) => value === option.id"
          @input="changeAction('salary_brutto', 'setInputValue', ['salary_brutto', $event])"
        />
        <BaseInputError :value="inputs['salary_brutto'].error" class="mb-1" />
        <div ref="input-salary" class="edit-group__card edit-group__row">
          <template v-if="!isExactSalary">
            <BaseInput :label="$tNewVacancy2('from')"
                       :value="inputs.salary.value[0]"
                       type="number"
                       :placeholder="fromSalaryPlaceholder"
                       @input="onSalaryInput($event, 'from')"
                       class="edit-group__item _white _shadow _left-label"
            />
            <BaseInput :label="$tNewVacancy2('before')"
                       :value="inputs.salary.value[1]"
                       type="number"
                       :placeholder="toSalaryPlaceholder"
                       @input="onSalaryInput($event, 'to')"
                       class="edit-group__item _white _shadow _left-label"/>
          </template>
          <BaseInput
            v-else
            :value="inputs.salary.value"
            type="number"
            :placeholder="formSalaryPlaceholder($tNewVacancy2('placeholder_salary_exact'))"
            @input="onSalaryInput($event)"
            class="edit-group__item _white _shadow"
            style="max-width: 231px;"
          />
        </div>
        <BaseInputError :value="inputs.salary.error" style="margin-top: -1rem;" />
      </div>
      <div v-if="!notActive"
           ref="input-salary_payout_type"
           class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('salary_payout_type') }}
          </span>
        </div>
        <div class="edit-group__card">
          <ButtonCheckList :value="inputs['salary_payout_type'].value"
                           :error="inputs['salary_payout_type'].error"
                           :required="inputs['salary_payout_type'].required"
                           :options="salaryPayoutTypes"
                           type="radio"
                           compareType="id"
                           text-key="name"
                           @input="changeAction('salary_payout_type', 'setInputValue', ['salary_payout_type', $event])"
                           class="edit-group__row _blue"/>
        </div>
      </div>
      <div ref="input-salary_additional_info" class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('salary_additional_info') }}
          </span>
          <SwitchCheckbox
            :value="!inputs.salary_additional_info.disabled"
            :label="$tNewVacancy2('Specify additional payment terms')"
            class="_blue"
            @input="toggleDisabled('salary_additional_info', $event)"
          />
        </div>
        <div v-if="!inputs.salary_additional_info.disabled" class="edit-group__card">
          <TranslateFiled :label="$tNewVacancy2('Describe important points')"
                          :value="inputs['salary_additional_info'].value"
                          :error="inputs['salary_additional_info'].error"
                          :required="inputs['salary_additional_info'].required"
                          :use-translate-buttons="useFieldsTranslations"
                          :use-editor-headings="useEditorsHeadings"
                          :original-locale="originalLocale"
                          textarea
                          editor
                          @input="changeAction('salary_additional_info', 'setInputValue', ['salary_additional_info', $event])"
                          @blur="saveAction('salary_additional_info', ['salary_additional_info', $event])"
                          class="_white"/>
        </div>
      </div>
    </div>

    <!--    <div class="vacancy-section-basic__section _date-start-work edit-group" data-edit-group="3">-->
    <!--      <div class="edit-group__head _blue">-->
    <!--        <h4 class="edit-group__title">-->
    <!--          {{ $tNewVacancy2('work_start') }}-->
    <!--        </h4>-->
    <!--        <SwitchCheckbox-->
    <!--            v-model="isExactWorkStart"-->
    <!--            :label="$tNewVacancy2('Specify the exact date')"-->
    <!--            @input="onWorkStartExactVariantChange"-->
    <!--            class="_blue"/>-->
    <!--      </div>-->
    <!--      <div class="edit-group__row">-->
    <!--        <template v-if="!isExactWorkStart">-->
    <!--          <DatePiker :prefix="$tNewVacancy2('from')"-->
    <!--                     :value="inputs.work_start.value[0]"-->
    <!--                     :placeholder="$tNewVacancy2('placeholder_work_start_from')"-->
    <!--                     @input="onWorkStartInput($event, 'from')"-->
    <!--                     class="edit-group__item _label"/>-->
    <!--          <DatePiker :prefix="$tNewVacancy2('before')"-->
    <!--                     :value="inputs.work_start.value[1]"-->
    <!--                     :placeholder="$tNewVacancy2('placeholder_work_start_to')"-->
    <!--                     @input="onWorkStartInput($event, 'to')"-->
    <!--                     class="edit-group__item _label"/>-->
    <!--        </template>-->
    <!--        <DatePiker v-else-->
    <!--                   key="exact"-->
    <!--                   :placeholder="$tNewVacancy2('placeholder_work_start_exact')"-->
    <!--                   :prefix="$tNewVacancy2('from')"-->
    <!--                   :value="inputs.work_start.value"-->
    <!--                   @input="onWorkStartInput($event)"-->
    <!--                   class="edit-group__item _label"/>-->
    <!--      </div>-->
    <!--    </div>-->

    <div class="vacancy-section-basic__section" data-edit-group="3">
      <TranslateFiled
        ref="input-work_start_text"
        :title="$tNewVacancy2('work_start_text')"
        :value="inputs['work_start_text'].value"
        :error="inputs['work_start_text'].error"
        :placeholder="$tNewVacancy2('placeholder_work_start_text')"
        :use-translate-buttons="useFieldsTranslations"
        :use-editor-headings="useEditorsHeadings"
        :original-locale="originalLocale"
        maxlength="25"
        @input="changeAction('work_start_text', 'setInputValue', ['work_start_text', $event])"
        @blur="saveAction('work_start_text', ['work_start_text', $event])"
        class="vacancy-section-basic__translate-field"
      >
        <template v-slot:title-append>
          <BaseTooltip
            :text="$tNewVacancy2('work_start_tooltip')"
            class="vacancy-section-basic__tooltip ml-2"
          >
            <template v-slot:activator>
              <span class="vacancy-section-basic__tooltip-question">
              ?
            </span>
            </template>
          </BaseTooltip>
        </template>
      </TranslateFiled>
    </div>

    <div v-if="!notActive"
         class="vacancy-section-basic__section edit-group" data-edit-group="3">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('work_period') }}
        </h4>
      </div>
      <ButtonCheckList ref="input-work_period"
                       :value="inputs['work_period'].value"
                       :error="inputs['work_period'].error"
                       :required="inputs['work_period'].required"
                       :options="workPeriods"
                       compareType="id"
                       text-key="name"
                       @input="onWorkPeriodInput"
                       class="edit-group__row _blue _mobile-column _edit-not-active"/>
    </div>

    <div class="vacancy-section-basic__section edit-group" data-edit-group="3">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('relevant') }}
        </h4>
      </div>
      <div class="edit-group__row">
        <div class="edit-group__item">
          <DatePiker ref="input-relevant"
                     :value="inputs['relevant'].value"
                     :required="inputs['relevant'].required"
                     :placeholder="$tNewVacancy2('placeholder_relevant_to')"
                     :disabled-date="notBeforeToday"
                     :clearable="false"
                     @input="changeAction('relevant', 'setInputValue', ['relevant', $event])"
                     class="_blue"
                     style="max-width: 262px;"/>
          <BaseInputError :value="inputs['relevant'].error"/>
        </div>

      </div>
    </div>
    <div class="vacancy-section-basic__section edit-group">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('work_contract') }}
        </h4>
      </div>
      <ButtonCheckList ref="input-work_contract"
                       :value="inputs.work_contract.value"
                       :error="inputs['work_contract'].error"
                       :required="inputs['work_contract'].required"
                       :options="workContracts"
                       type="radio"
                       compareType="id"
                       text-key="name"
                       @input="onWorkContractInput"
                       class="edit-group__row _blue _mobile-column"/>
      <BaseSelect ref="input-work_contract_subitem"
                  v-if="inputs.work_contract.value && inputs.work_contract.value.need_action"
                  :label="$tNewVacancy2('Type')"
                  :value="inputs.work_contract_subitem.value"
                  :error="inputs['work_contract_subitem'].error"
                  :required="inputs['work_contract_subitem'].required"
                  :options="workContractSubItems"
                  textKey="name"
                  :placeholder="$tNewVacancy2('placeholder_contract_type')"
                  @input="changeAction('work_contract_subitem', 'setInputValue', ['work_contract_subitem', $event])"

      />
    </div>

    <div class="vacancy-section-basic__section vacancy-section-basic__section-time-work edit-group">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('schedule_shift_duration') }}
        </h4>
      </div>
      <div class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('Shift options') }}
          </span>
        </div>
        <div class="edit-group__card _small">
          <ButtonCheckList
            ref="input-schedule_shift_duration"
            :value="inputs.schedule_shift_duration.value"
            :error="inputs.schedule_shift_duration.error"
            :required="inputs.schedule_shift_duration.required"
            :options="scheduleWorkDurations"
            compareType="id"
            text-key="name"
            @input="changeAction('schedule_shift_duration', 'setInputValue', ['schedule_shift_duration', $event])"
            class="edit-group__row _blue _three-columns"/>
        </div>
      </div>
      <div class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('Availability of night shifts') }}
          </span>
        </div>
        <div class="edit-group__card _small">
          <ButtonCheckList ref="input-schedule_night_shifts"
                           :value="inputs.schedule_night_shifts.value"
                           :error="inputs.schedule_night_shifts.error"
                           :options="yesOrNoWithNoSpecify"
                           type="radio"
                           compareType="id"
                           :compare="compareIdOrValue"
                           text-key="name"
                           @input="changeAction('schedule_night_shifts', 'setInputValue', ['schedule_night_shifts', $event])"
                           class="edit-group__row _blue"/>
        </div>
      </div>
      <div class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('The ability to work overtime') }}
          </span>
        </div>
        <div class="edit-group__card _small">
          <ButtonCheckList ref="input-schedule_overtime_shifts"
                           :value="inputs.schedule_overtime_shifts.value"
                           :error="inputs.schedule_overtime_shifts.error"
                           :options="yesOrNoWithNoSpecify"
                           type="radio"
                           compareType="id"
                           :compare="compareIdOrValue"
                           text-key="name"
                           @input="changeAction('schedule_overtime_shifts', 'setInputValue', ['schedule_overtime_shifts', $event])"
                           class="edit-group__row _blue"/>
        </div>
      </div>
      <div ref="input-schedule_additional" class="edit-group__sub-content">
        <div class="edit-group__sub-head">
          <span class="edit-group__sub-title">
            {{ $tNewVacancy2('Additional description') }}
          </span>
          <SwitchCheckbox
            :value="!inputs.schedule_additional.disabled"
            :label="$tNewVacancy2('Specify an additional description')"
            class="_blue"
            @input="toggleDisabled('schedule_additional', $event)"
          />
        </div>
        <div v-if="!inputs.schedule_additional.disabled" class="edit-group__card">
          <TranslateFiled :label="$tNewVacancy2('Describe important points')"
                          :value="inputs.schedule_additional.value"
                          :error="inputs.schedule_additional.error"
                          :use-translate-buttons="useFieldsTranslations"
                          :use-editor-headings="useEditorsHeadings"
                          :original-locale="originalLocale"
                          textarea
                          editor
                          @input="changeAction('schedule_additional', 'setInputValue', ['schedule_additional', $event])"
                          @blur="saveAction('schedule_additional', ['schedule_additional', $event])"
                          class="_white"
          />
        </div>
      </div>
    </div>

    <div class="vacancy-section-basic__section edit-group" data-edit-group="4">
      <div class="edit-group__head _blue">
        <h4 class="edit-group__title">
          {{ $tNewVacancy2('Benefits') }}
        </h4>
      </div>
      <BenefitsCollapse
        ref="input-conditions_observation_housing"
        icon-name="ico-virus"
        :title="$tNewVacancy2('conditions_observation_housing')"
        :filled="isFilledObservationHousing"
        class="vacancy-section-basic__collapse">
        <TranslateFiled
          :label="$tNewVacancy2('Describe important points')"
          :value="inputs.conditions_observation_housing.value"
          :use-translate-buttons="useFieldsTranslations"
          :use-editor-headings="useEditorsHeadings"
          :original-locale="originalLocale"
          textarea
          editor
          :placeholder="$tNewVacancy2('conditions_observation_housing_placeholder')"
          @input="changeAction('conditions_observation_housing', 'setInputValue', ['conditions_observation_housing', $event])"
          class="vacancy-section-basic__translate-collapse _white"
        />
        <ButtonCheckList
          :value="inputs.conditions_observation_housing_type.value"
          :options="conditionsType"
          type="radio"
          compareType="id"
          text-key="name"
          @input="changeAction('conditions_observation_housing_type', 'setInputValue', ['conditions_observation_housing_type', $event])"
          class="_blue _three-columns"/>
        <div v-if="!notActive"
             class="edit-group__card-files" style="margin-top: 3rem">
          <!--          <div class="edit-group__label">-->
          <!--            {{$tNewVacancy2('observation_housing_files')}}-->
          <!--          </div>-->
          <EditFilesDraggable ref="input-observation_housing_files"
                              class="_edit-not-active"
                              :value="observationHousingFiles"
                              :pending="observationHousingFilesPending"
                              :pending-edit="observationHousingFilesEditPending"
                              :error="observationHousingFilesError"
                              :with-caption="false"
                              @input="uploadFilesByKey(['observationHousingFiles', 'observation_housing_files', $event])"
                              @update="updateFilesByKey(['observationHousingFiles', 'observation_housing_files', $event])"
                              @delete="deleteFilesByKey(['observationHousingFiles', 'observation_housing_files', $event])"/>
        </div>
      </BenefitsCollapse>
      <BenefitsCollapse ref="input-conditions_housing"
                        :title="$tNewVacancy2('conditions_housing')"
                        icon-name="ico-house"
                        :filled="isFilledHousing"
                        class="vacancy-section-basic__collapse">
        <MapPicker class="_white"
                   :value="inputs.conditions_housing.value"
                   :label="$tNewVacancy2('Locality')"
                   :placeholder="$tNewVacancy2('placeholder_map')"
                   @input="changeAction('conditions_housing', 'setInputValue', ['conditions_housing', $event])"
                   style="margin-bottom: 2rem;"/>
        <TranslateFiled
          :label="$tNewVacancy2('Describe important points')"
          :value="inputs.conditions_housing_description.value"
          :placeholder="$tNewVacancy2('conditions_housing_placeholder')"
          :use-translate-buttons="useFieldsTranslations"
          :use-editor-headings="useEditorsHeadings"
          :original-locale="originalLocale"
          textarea
          editor
          @input="changeAction('conditions_housing_description', 'setInputValue', ['conditions_housing_description', $event])"
          class="vacancy-section-basic__translate-collapse _white"
        />
        <ButtonCheckList
          :value="inputs.conditions_housing_type.value"
          :options="conditionsType"
          type="radio"
          compareType="id"
          text-key="name"
          @input="changeAction('conditions_housing_type', 'setInputValue', ['conditions_housing_type', $event])"
          class="_blue _three-columns"/>
        <div v-if="!notActive"
             class="edit-group__card-files" style="margin-top: 3rem">
          <!--          <div class="edit-group__label">-->
          <!--            {{$tNewVacancy2('housing_files')}}-->
          <!--          </div>-->
          <EditFilesDraggable class="_edit-not-active"
                              :value="housingFiles"
                              :pending="housingFilesPending"
                              :pending-edit="housingFilesEditPending"
                              :error="housingFilesError"
                              :with-caption="false"
                              @input="uploadFilesByKey(['housingFiles', 'housing_files', $event])"
                              @update="updateFilesByKey(['housingFiles', 'housing_files', $event])"
                              @delete="deleteFilesByKey(['housingFiles', 'housing_files', $event])"/>
        </div>
      </BenefitsCollapse>
      <BenefitsCollapse
        ref="input-conditions_meals"
        icon-name="ico-cutlery"
        :title="$tNewVacancy2('conditions_meals')"
        :filled="isFilledMeals"
        class="vacancy-section-basic__collapse">
        <TranslateFiled
          :label="$tNewVacancy2('Describe important points')"
          :value="inputs.conditions_meals.value"
          :placeholder="$tNewVacancy2('conditions_meals_placeholder')"
          :use-translate-buttons="useFieldsTranslations"
          :use-editor-headings="useEditorsHeadings"
          :original-locale="originalLocale"
          textarea
          editor
          @input="changeAction('conditions_meals', 'setInputValue', ['conditions_meals', $event])"
          class="vacancy-section-basic__translate-collapse _white"
        />
        <ButtonCheckList
          ref="input-conditions_meals_type"
          :value="inputs.conditions_meals_type.value"
          :options="conditionsType"
          type="radio"
          compareType="id"
          text-key="name"
          @input="changeAction('conditions_meals_type', 'setInputValue', ['conditions_meals_type', $event])"
          class="_blue _three-columns"/>
      </BenefitsCollapse>
      <BenefitsCollapse
        ref="input-conditions_transport"
        icon-name="ico-transport"
        :filled="isFilledTransport"
        :title="$tNewVacancy2('conditions_transport')"
        class="vacancy-section-basic__collapse">
        <TranslateFiled
          :label="$tNewVacancy2('Describe important points')"
          :value="inputs.conditions_transport.value"
          :placeholder="$tNewVacancy2('conditions_transport_placeholder')"
          :use-translate-buttons="useFieldsTranslations"
          :use-editor-headings="useEditorsHeadings"
          :original-locale="originalLocale"
          textarea
          editor
          @input="changeAction('conditions_transport', 'setInputValue', ['conditions_transport', $event])"
          class="vacancy-section-basic__translate-collapse _white"
        />
        <ButtonCheckList
          :options="conditionsType"
          :value="inputs.conditions_transport_type.value"
          type="radio"
          compareType="id"
          text-key="name"
          @input="changeAction('conditions_transport_type', 'setInputValue', ['conditions_transport_type', $event])"
          class="_blue _three-columns"/>
        <div v-if="!notActive"
             class="edit-group__card-files" style="margin-top: 3rem">
          <!--          <div class="edit-group__label">-->
          <!--            {{$tNewVacancy2('transport_files')}}-->
          <!--          </div>-->
          <EditFilesDraggable class="_edit-not-active"
                              :value="transportFiles"
                              :pending="transportFilesPending"
                              :pending-edit="transportFilesEditPending"
                              :error="transportFilesError"
                              :with-caption="false"
                              @input="uploadFilesByKey(['transportFiles', 'transport_files', $event])"
                              @update="updateFilesByKey(['transportFiles', 'transport_files', $event])"
                              @delete="deleteFilesByKey(['transportFiles', 'transport_files', $event])"/>
        </div>
      </BenefitsCollapse>
      <BenefitsCollapse
        ref="input-conditions_clothes"
        icon-name="ico-t-shirt"
        :filled="isFilledClothes"
        :title="$tNewVacancy2('conditions_clothes')"
        class="vacancy-section-basic__collapse">
        <TranslateFiled
          :label="$tNewVacancy2('Describe important points')"
          :placeholder="$tNewVacancy2('conditions_clothes_placeholder')"
          :use-translate-buttons="useFieldsTranslations"
          :use-editor-headings="useEditorsHeadings"
          :original-locale="originalLocale"
          textarea
          editor
          :value="inputs.conditions_clothes.value"
          @input="changeAction('conditions_clothes', 'setInputValue', ['conditions_clothes', $event])"
          class="vacancy-section-basic__translate-collapse _white"
        />
        <ButtonCheckList
          :value="inputs.conditions_clothes_type.value"
          :options="conditionsType"
          type="radio"
          compareType="id"
          text-key="name"
          @input="changeAction('conditions_clothes_type', 'setInputValue', ['conditions_clothes_type', $event])"
          class="_blue _three-columns"/>
        <div v-if="!notActive"
             class="edit-group__card-files" style="margin-top: 3rem">
          <!--          <div class="edit-group__label">-->
          <!--            {{$tNewVacancy2('clothes_files')}}-->
          <!--          </div>-->
          <EditFilesDraggable class="_edit-not-active"
                              :value="clothesFiles"
                              :pending="clothesFilesPending"
                              :pending-edit="clothesFilesEditPending"
                              :error="clothesFilesError"
                              :with-caption="false"
                              @input="uploadFilesByKey(['clothesFiles', 'clothes_files', $event])"
                              @update="updateFilesByKey(['clothesFiles', 'clothes_files', $event])"
                              @delete="deleteFilesByKey(['clothesFiles', 'clothes_files', $event])"/>
        </div>
      </BenefitsCollapse>
      <BenefitsCollapse
        ref="input-conditions_additional"
        icon-name="ico-plus"
        :filled="isFilledAdditional"
        :title="$tNewVacancy2('conditions_additional')"
        class="vacancy-section-basic__collapse">
        <TranslateFiled
          :label="$tNewVacancy2('Describe important points')"
          :value="inputs.conditions_additional.value"
          :placeholder="$tNewVacancy2('conditions_additional_placeholder')"
          :use-translate-buttons="useFieldsTranslations"
          :use-editor-headings="useEditorsHeadings"
          :original-locale="originalLocale"
          textarea
          editor
          @input="changeAction('conditions_additional', 'setInputValue', ['conditions_additional', $event])"
          class="vacancy-section-basic__translate-collapse _white"
        />
      </BenefitsCollapse>
    </div>
  </div>
</template>

<script>
  import {mapState, mapMutations, mapActions, mapGetters} from 'vuex'
  import TranslateFiled from '../TranslateFiled'
  import ButtonCheckList from '../ButtonCheckList'
  import Amount from '../Amount'
  import SwitchCheckbox from '../SwitchCheckbox'
  import DatePiker from '../DatePiker'
  import SCategory from '../loadingSelects/SCategory'
  import SDirection from '../loadingSelects/SDirection'
  import BenefitsCollapse from './BenefitsCollapse'
  import MapPicker from './MapPicker'
  import EditFilesDraggable from './EditFilesDraggable'
  import BaseInputError from '@/components/BaseInputError'

  let timer = null

  export default {
    name: "EditSectionBase",
    components: {
      BaseInputError,
      TranslateFiled,
      ButtonCheckList,
      Amount,
      SwitchCheckbox,
      DatePiker,
      SCategory,
      SDirection,
      BenefitsCollapse,
      MapPicker,
      EditFilesDraggable,
    },
    props: [
      'selectedGroupId',
    ],
    data: () => ({
      isExactSalary: false,
      isExactWorkStart: false,
      minQuantity: location && location.hostname === 'localhost' ? 0 : 1,

      notActive: true // hide blocks
    }),
    computed: {
      ...mapState({
        currencies(state) {
          return state.currencies.filter(currency => currency.currency !== 'ALL')
        },
      }),
      ...mapGetters('user', [
        'isOur',
        'employerProfile',
      ]),
      ...mapState('vacancyEdit', {
        'salaryTypes'(state) {
          return state.salaryTypes
        },
        'salaryPayoutTypes'(state) {
          return this.addNoSpecifyItemForList(state.salaryPayoutTypes)
        },
        'workPeriods'(state) {
          return this.addNoSpecifyItemForList(state.workPeriods)
        },
        'workContracts'(state) {
          return this.addNoSpecifyItemForList(state.workContracts)
        },
        'workContractSubItems'(state) {
          return this.addNoSpecifyItemForList(state.workContractSubItems)
        },
        'scheduleWorkDurations': 'scheduleWorkDurations',
        'conditionsType'(state) {
          return this.addNoSpecifyItemForList(state.conditionsType)
        },
      }),
      ...mapState('vacancyEdit/sectionBase', [
        'inputs',
        'categories',

        'observationHousingFiles',
        'observationHousingFilesPending',
        'observationHousingFilesEditPending',
        'observationHousingFilesError',

        'housingFiles',
        'housingFilesPending',
        'housingFilesEditPending',
        'housingFilesError',

        'transportFiles',
        'transportFilesPending',
        'transportFilesEditPending',
        'transportFilesError',

        'clothesFiles',
        'clothesFilesPending',
        'clothesFilesEditPending',
        'clothesFilesError',
      ]),
      ...mapGetters('vacancyEdit', [
        'yesOrNo',
        'originalLocale',
        'afterJustCreated',
        'savedBlocks',
      ]),
      useFieldsTranslations() {
        return this.isOur && this.savedBlocks?.[1] === true && !this.afterJustCreated;
      },
      yesOrNoWithNoSpecify() {
        return this.addNoSpecifyItemForList(this.yesOrNo)
      },
      defaultChekListValue() {
        return {
          id: null,
          name: this.$tNewVacancy2('No specified')
        }
      },
      defaultSalaryTypeValue() {
        return this.salaryTypes?.find(t => t.id === 80);
      },
      defaultCurrencyValue() {
        if (this.employerProfile?.country?.id === 165) return this.currencies.find(c => c.id === 4);
        return this.currencies.find(c => c.id === 5);
      },
      categoriesIds() {
        return this.categories.map(item => item.id)
      },
      isFilledObservationHousing() {
        const inputs = this.inputs
        return !!(isFilledTranslate(inputs.conditions_observation_housing.value)
          || this.$get(inputs.conditions_observation_housing_type, 'value.id'))
      },
      isFilledHousing() {
        const inputs = this.inputs
        return !!(inputs.conditions_housing.value
          || isFilledTranslate(inputs.conditions_housing_description.value)
          || this.$get(inputs.conditions_housing_type, 'value.id'))
      },
      isFilledMeals() {
        const inputs = this.inputs
        return !!(isFilledTranslate(inputs.conditions_meals.value)
          || this.$get(inputs.conditions_meals_type, 'value.id'))
      },
      isFilledTransport() {
        const inputs = this.inputs
        return !!(isFilledTranslate(inputs.conditions_transport.value)
          || this.$get(inputs.conditions_transport_type, 'value.id'))
      },
      isFilledClothes() {
        const inputs = this.inputs
        return !!(isFilledTranslate(inputs.conditions_clothes.value)
          || this.$get(inputs.conditions_clothes_type, 'value.id'))
      },
      isFilledAdditional() {
        const inputs = this.inputs
        return !!(isFilledTranslate(inputs.conditions_additional.value))
      },
      useEditorsHeadings() {
        return false;
      },
      salaryNetGrossOptions() {
        return [
          {
            id: false,
            label: this.$tNewVacancy2('Net'),
          },
          {
            id: true,
            label: this.$tNewVacancy2('Gross'),
          },
        ];
      },
      showCustomProfession() {
        return this.inputs['profession'].value?.id === 291;
      },
      fromSalaryPlaceholder() {
        return this.formSalaryPlaceholder(this.$tNewVacancy2('placeholder_salary_from'));
      },
      toSalaryPlaceholder() {
        return this.formSalaryPlaceholder(this.$tNewVacancy2('placeholder_salary_to'));
      },
    },
    mounted() {
      const interval = setInterval(() => {
        const requiredFields = [this.defaultSalaryTypeValue, this.defaultCurrencyValue, this.defaultChekListValue];
        if (!requiredFields.every(v => v)) return;

        this.applyDefaultValues();
        this.saveSection().then(() => this.applyDefaultDisabled());

        clearInterval(interval);
      }, 700);
    },
    methods: {
      ...mapMutations('vacancyEdit/sectionBase', [
        'setProperty',
        'setInputValue',
        'addCategory',
        'setCategoriesByProfession',
        'setDefaultInputsValues',
        'setDefaultInputsDisabled',
        'setInputDisabled',
      ]),
      ...mapActions('vacancyEdit/sectionBase', [
        'uploadFilesByKey',
        'updateFilesByKey',
        'deleteFilesByKey',
      ]),

      saveSection() {
        return this.$store.dispatch('vacancyEdit/saveSection', { sectionName: 'sectionBase' });
      },
      applyDefaultValues() {
        const entries = [
          ['salary_type', this.defaultSalaryTypeValue],
          ['currency', this.defaultCurrencyValue],
          ['salary_payout_type', this.defaultChekListValue],
          ['work_contract', this.defaultChekListValue],
          ['schedule_night_shifts', this.defaultChekListValue],
          ['conditions_observation_housing_type', this.defaultChekListValue],
          ['schedule_overtime_shifts', this.defaultChekListValue],
          ['conditions_housing_type', this.defaultChekListValue],
          ['conditions_meals_type', this.defaultChekListValue],
          ['conditions_transport_type', this.defaultChekListValue],
          ['conditions_clothes_type', this.defaultChekListValue],
        ];
        this.setDefaultInputsValues(entries);
      },
      saveAction(key, value) {

        let val;
        if (value) {
          let values = value['1'].replace(/["'{}]/g, '').replace(/,/g, ', ') || 'empty'
          val = `${values}`
        }
        if (val !== undefined) {
          let str = `Vacancy, Main information. Field: ${key}, value: ${val}`
          this.$store.dispatch('sendAlert', {
            type: 'form',
            title: str
          })
        }

      },
      changeAction(key, method, value) {
        if (method) {
          this[method](value)
        }
        let val;
        if (value && value instanceof Object) {
          if (key === 'quantity') {
            val = value['1']
          } else if (key === 'relevant') {
            val = value
          } else if (key === 'categories') {
            val = value['1'].map((c) => {
              return c.title
            }).toString() || 'empty'
          } else if (key === 'currency') {
            val = value['1']['currency']
          } else if (value['1'] !== null && value['1'].category) {
            val = value['1'].category.title
          } else if (value['1'] !== null && value['1'].name) {
            val = value['1'].name
          } else if (value['1'] !== null && value['1'] instanceof Array) {
            val = value['1'].map((v) => {
              return v.name
            }).toString()
          }
        }

        clearTimeout(timer)
        timer = setTimeout(() => {
          this.saveSection();

          if (val !== undefined) {
            let str = `Vacancy, Main information. Field: ${key}, value: ${val}`
            this.$store.dispatch('sendAlert', {
              type: 'form',
              title: str
            })
          }
        }, 1000)
      },
      toggleDisabled(inputKey, value) {
        this.setInputDisabled([inputKey, !value]);
      },
      applyDefaultDisabled() {
        const keys = ['salary_additional_info', 'schedule_additional'];
        this.setDefaultInputsDisabled(keys);
      },
      compareIdOrValue(value, option) {
        if (typeof value === 'object') return value.id === option.id;
        return value === option.id;
      },
      formSalaryPlaceholder(prependText) {
        const netGrossValue = this.inputs['salary_brutto'].value;
        const item = this.salaryNetGrossOptions.find(item => {
          return item.id === (typeof netGrossValue === 'boolean' ? netGrossValue : netGrossValue?.id);
        });

        if (!item) return prependText;
        return prependText + ` (${item?.label.toLowerCase()})`;
      },
      onProfessionInput(value) {
        this.setCategoriesByProfession(value)
        this.changeAction('profession', 'setInputValue', ['profession', value])
      },
      onAddCategory(category) {
        this.addCategory(category)
        this.changeAction('categories')
      },
      categoriesSelectFilter(options) {
        const categoriesIds = this.categoriesIds

        return (options || []).filter(item => !categoriesIds.includes(item.id))
      },
      onSalaryInput(value, key) {
        if (!key) {
          this.setInputValue(['salary', value || null])
          return this.changeAction('salary')
        }

        const result = Array.isArray(this.inputs.salary.value) ? [...this.inputs.salary.value] : []

        if (key === 'from') {
          result[0] = value || null
        } else {
          result[1] = value || null
        }

        this.setInputValue(['salary', result])
        this.changeAction('salary')
      },
      onSalaryExactVariantChange() {
        if (this.isExactSalary) {
          let value = this.inputs.salary.value[0] || this.inputs.salary.value[1] || null
          this.setInputValue(['salary', value])
        } else {
          let value = this.inputs.salary.value || null
          this.setInputValue(['salary', [value]])
        }

        this.changeAction('salary')
      },
      onWorkStartInput(value, key) {
        if (!key) {
          this.setInputValue(['work_start', value])
          this.changeAction('work_start')
          return
        }

        const result = Array.isArray(this.inputs.work_start.value) ? [...this.inputs.work_start.value] : []

        if (key === 'from') {
          result[0] = value
        } else {
          result[1] = value
        }

        this.setInputValue(['work_start', result])
        this.changeAction('work_start')
      },
      onWorkStartExactVariantChange() {
        if (this.isExactWorkStart) {
          let value = this.inputs.work_start.value[0] || this.inputs.work_start.value[1] || null
          this.setInputValue(['work_start', value])
        } else {
          let value = this.inputs.work_start.value || null
          this.setInputValue(['work_start', [value]])
        }

        this.changeAction('work_start')
      },
      notBeforeToday(date) {
        return date < new Date(new Date().setHours(0, 0, 0, 0));
      },
      onWorkContractInput(value) {
        this.setInputValue(['work_contract', value])

        if (!value.need_action) {
          this.setInputValue(['work_contract_subitem', null])
          this.changeAction('work_contract_subitem')
        } else {
          this.changeAction('work_contract')
        }
      },
      onWorkPeriodInput(value) {
        const haveAll = this.inputs.work_period.value && this.inputs.work_period.value.find(item => item.need_action)
        const haveValueAll = value && value.find(item => item.need_action)

        if (!haveAll && haveValueAll) {
          value = [...this.workPeriods]
        } else if (haveValueAll && haveAll) {
          value = value.filter(item => !item.need_action)
        }

        this.changeAction('work_period', 'setInputValue', ['work_period', value])
      },
      addNoSpecifyItemForList(list) {
        return [this.defaultChekListValue, ...(list || [])]
      },
    },
    created() {
      this.isExactSalary = !Array.isArray(this.inputs.salary.value)
    }
  }

  function isFilledTranslate(value) {
    if (!value) return false

    value = JSON.parse(value)
    for (const key in value) {
      if (value[key]) return true
    }

    return false
  }
</script>

<style scoped>

</style>
